import { Control, Controller, FieldError } from 'react-hook-form';
import { AutomationFormData } from '../../validations/automationForm';
import { useInstagramPosts } from '../../queries/useAutomationQueries';
import { useInstagramIntegration } from '../../hooks/useInstagramIntegration';
import { useModal } from '@/hooks/useModal';
import { PostPickerButton } from './PostPickerButton';
import { PostPickerModal } from './PostPickerModal';

interface PostPickerProps {
    control: Control<AutomationFormData>;
    error?: FieldError;
}

/**
 * PostPicker - Wrapper component for post selection
 * Combines PostPickerButton and PostPickerModal
 */
export const PostPicker = ({ control, error }: PostPickerProps) => {
    const { isOpen, open, close } = useModal();
    const { instagramStatus } = useInstagramIntegration();
    const isInstagramConnected = instagramStatus === 'connected';
    const { data: postsResponse } = useInstagramPosts(isInstagramConnected);
    const posts = postsResponse?.data || [];

    return (
        <div className='flex flex-col gap-2 md:gap-4'>
            <div className='space-y-2 max-md:hidden'>
                <p className='text-label-sm-bold mb-1'>Select posts (optional)</p>
                <p className='text-label-sm-regular text-gray'>
                    Choose specific posts to trigger this automation, or leave empty to trigger on all posts.
                </p>
            </div>

            <Controller
                name='media_ids'
                control={control}
                render={({ field }) => {
                    const selectedIds = field.value || [];

                    // Get URLs for selected posts
                    const selectedPostUrls = selectedIds
                        .map((id: string) => {
                            const post = posts.find(p => p.id === id);
                            return post?.thumbnail_url || post?.media_url || '';
                        })
                        .filter(Boolean);

                    const handleConfirm = (postIds: string[]) => {
                        field.onChange(postIds);
                    };

                    return (
                        <>
                            <PostPickerButton selectedPostUrls={selectedPostUrls} onClick={open} />

                            <PostPickerModal
                                isOpen={isOpen}
                                onClose={close}
                                selectedPostIds={selectedIds}
                                onConfirm={handleConfirm}
                            />
                        </>
                    );
                }}
            />

            {error && <p className='text-caption-1-regular text-negative mt-1'>{error.message}</p>}
        </div>
    );
};
